import { useState } from 'react';
import type { InvitationInfo, MemberInfo, OrgRole } from '@shared/types';
import { formatRelative } from '@shared/format';
import { ROLE_LABELS } from '@shared/roles';
import { useAuthStore } from '../app/authStore';
import { ApiError, api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, CardHeader, CopyField, PageHeader } from '../components/ui';
import { Field, Input, Notice, Select } from '../components/form';
import { ConfirmDialog, Modal, useToast } from '../components/feedback';
import { Icon } from '../components/Icon';
import { Table, type Column } from '../components/table';

const ASSIGNABLE: { value: OrgRole; label: string }[] = [
  { value: 'admin', label: '管理员' },
  { value: 'member', label: '成员' },
  { value: 'viewer', label: '观察员' },
];

/**
 * 成员与邀请。
 *
 * 「邀请」在本系统里不是发邮件，而是**生成一条一次性链接**由管理员手工转达 ——
 * 因为没有邮件服务。界面上必须说清这一点，否则用户会去等一封永远不会到的邮件。
 */
export function MembersPage() {
  const session = useAuthStore((state) => state.session);
  const can = useAuthStore((state) => state.can);
  const toast = useToast();

  const members = useAsync<{ members: MemberInfo[] }>(() => api.get('/api/members'), []);
  const invitations = useAsync<{ invitations: InvitationInfo[] }>(
    () => api.get('/api/invitations'),
    [],
  );

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('member');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);

  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MemberInfo | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const manage = can('member.manage');
  const invite = can('member.invite');

  async function run(action: () => Promise<unknown>, success: string, id?: string) {
    if (id) setBusyId(id);
    try {
      await action();
      toast.push(success, 'ok');
      await Promise.all([members.reload(), invitations.reload()]);
    } catch (error) {
      toast.push(describeError(error), 'danger');
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<MemberInfo>[] = [
    {
      key: 'name',
      header: '姓名',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.name}</p>
          <p className="truncate text-xs text-fg-muted">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: '角色',
      render: (row) =>
        manage && row.role !== 'owner' && row.userId !== session?.user.id ? (
          <Select
            className="h-8 w-[7.5rem] text-[13px]"
            value={row.role}
            options={ASSIGNABLE}
            onChange={(role) =>
              void run(
                () => api.patch(`/api/members/${row.userId}`, { role }),
                `已将 ${row.name} 的角色改为${ROLE_LABELS[role]}`,
                row.userId,
              )
            }
          />
        ) : (
          <Badge tone={row.role === 'owner' ? 'brand' : 'neutral'}>{ROLE_LABELS[row.role]}</Badge>
        ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) =>
        row.status === 'active' ? (
          row.totpEnabled ? (
            <Badge tone="ok">已开启两步验证</Badge>
          ) : (
            <Badge>未开启两步验证</Badge>
          )
        ) : (
          <Badge tone="warn">已停用</Badge>
        ),
    },
    {
      key: 'lastLogin',
      header: '最近登录',
      render: (row) => <span className="text-fg-muted">{formatRelative(row.lastLoginAt)}</span>,
    },
    {
      key: 'actions',
      header: '操作',
      hideOnMobile: true,
      render: (row) => {
        const isSelf = row.userId === session?.user.id;
        if (!manage || isSelf || row.role === 'owner') {
          return <span className="text-xs text-fg-subtle">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant="link"
              onClick={() =>
                void run(
                  async () => {
                    const result = await api.post<{ temporaryPassword: string; email: string }>(
                      `/api/members/${row.userId}/password`,
                      {},
                    );
                    setResetResult({ email: result.email, password: result.temporaryPassword });
                  },
                  '已重置临时口令',
                  row.userId,
                )
              }
            >
              重置口令
            </Button>
            <Button
              size="sm"
              variant="link"
              onClick={() =>
                void run(
                  () => api.post(`/api/members/${row.userId}/sessions`, {}),
                  '已结束该成员在本组织的全部会话',
                  row.userId,
                )
              }
            >
              退出设备
            </Button>
            <Button
              size="sm"
              variant="link"
              onClick={() =>
                void run(
                  () =>
                    api.patch(`/api/members/${row.userId}`, {
                      status: row.status === 'active' ? 'suspended' : 'active',
                    }),
                  row.status === 'active' ? '已停用该成员' : '已启用该成员',
                  row.userId,
                )
              }
            >
              {row.status === 'active' ? '停用' : '启用'}
            </Button>
            <Button size="sm" variant="link" onClick={() => setConfirmRemove(row)}>
              <span className="text-danger">移出</span>
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="成员与邀请"
        description="成员的角色在组织内生效；同一个邮箱可以被多个组织邀请。"
        action={
          invite ? (
            <Button
              variant="primary"
              icon="plus"
              onClick={() => {
                setInviteLink(null);
                setInviteError(null);
                setInviteOpen(true);
              }}
            >
              邀请成员
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader
          title="成员"
          hint="角色、状态与最近登录时间"
          action={<Button size="sm" icon="refresh" onClick={() => void members.reload()}>刷新</Button>}
        />
        <Table
          caption="组织成员列表"
          columns={columns}
          rows={members.data?.members ?? []}
          getKey={(row) => row.userId}
          loading={members.loading}
          empty={{ title: '还没有成员', description: invite ? '点右上角「邀请成员」生成一条一次性邀请链接。' : undefined }}
        />
        {busyId ? <p className="mt-2 text-xs text-fg-subtle">正在处理…</p> : null}
      </Card>

      <Card>
        <CardHeader title="待接受的邀请" hint="本系统不发送邮件 —— 链接需要你通过可信渠道转达" />
        <Notice tone="info">
          邀请链接有效期 7 天、只能用一次。我们没有邮件服务，因此链接生成后请<strong>自己</strong>发给对方。
        </Notice>
        <div className="mt-4">
          {(invitations.data?.invitations.length ?? 0) === 0 ? (
            <p className="text-[13px] text-fg-muted">没有待接受的邀请。</p>
          ) : (
            <ul className="divide-y divide-line">
              {invitations.data?.invitations.map((item) => (
                <li key={item.id} className="flex flex-wrap items-center gap-3 py-3 text-[13px]">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-fg">{item.email}</p>
                    <p className="text-xs text-fg-muted">
                      {ROLE_LABELS[item.role]} · {item.invitedByName} 邀请 · 到期{' '}
                      {formatRelative(item.expiresAt)}
                      {item.acceptedAt ? ` · 已于 ${formatRelative(item.acceptedAt)} 接受` : ''}
                    </p>
                  </div>
                  {item.acceptedAt ? (
                    <Badge tone="ok">已接受</Badge>
                  ) : invite ? (
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() =>
                        void run(() => api.delete(`/api/invitations/${item.id}`), '邀请已撤销', item.id)
                      }
                    >
                      撤销
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* ------------------------------------------------------- 邀请弹窗 */}
      <Modal
        open={inviteOpen}
        title="邀请成员"
        description="生成一条一次性链接，由你转达给对方。"
        onClose={() => setInviteOpen(false)}
        footer={
          inviteLink ? (
            <Button variant="primary" onClick={() => setInviteOpen(false)}>
              完成
            </Button>
          ) : (
            <>
              <Button onClick={() => setInviteOpen(false)} disabled={inviteBusy}>
                取消
              </Button>
              <Button
                variant="primary"
                loading={inviteBusy}
                onClick={async () => {
                  setInviteBusy(true);
                  setInviteError(null);
                  try {
                    const result = await api.post<{ invitation: InvitationInfo & { inviteUrl: string } }>(
                      '/api/invitations',
                      { email: inviteEmail.trim(), role: inviteRole },
                    );
                    setInviteLink(result.invitation.inviteUrl);
                    setInviteEmail('');
                    await invitations.reload();
                  } catch (error) {
                    setInviteError(error instanceof ApiError ? error.message : '邀请失败');
                  } finally {
                    setInviteBusy(false);
                  }
                }}
              >
                生成邀请链接
              </Button>
            </>
          )
        }
      >
        {inviteLink ? (
          <div className="space-y-3">
            <Notice tone="ok">邀请链接已生成。它只会显示这一次，请立即复制并转达。</Notice>
            <CopyField label="邀请链接" value={inviteLink} />
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="邮箱" htmlFor="invite-email" required error={inviteError ?? undefined}>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={setInviteEmail}
                placeholder="newcomer@example.com"
              />
            </Field>
            <Field label="角色" hint="所有者不能通过邀请产生，需要在成员列表里转让。">
              <Select value={inviteRole} options={ASSIGNABLE} onChange={setInviteRole} />
            </Field>
          </div>
        )}
      </Modal>

      {/* --------------------------------------------------- 临时口令弹窗 */}
      <Modal
        open={resetResult !== null}
        title="临时口令已生成"
        description="它只会显示这一次。对方首次登录后必须立即修改。"
        onClose={() => setResetResult(null)}
        footer={
          <Button variant="primary" onClick={() => setResetResult(null)}>
            我已记下
          </Button>
        }
      >
        {resetResult ? (
          <div className="space-y-3">
            <Notice tone="warn">
              通过本方式重置后，该成员在所有设备上的登录已失效，且下次登录会被要求改密。
            </Notice>
            <CopyField label={`临时口令（${resetResult.email}）`} value={resetResult.password} />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={confirmRemove !== null}
        title={`将 ${confirmRemove?.name ?? ''} 移出组织？`}
        description="该成员在本组织的会话会立即失效，其账号本身仍然保留（可能仍属于其它组织）。"
        confirmLabel="移出组织"
        busy={busyId !== null}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          const target = confirmRemove;
          setConfirmRemove(null);
          if (target) {
            void run(() => api.delete(`/api/members/${target.userId}`), '已移出组织', target.userId);
          }
        }}
      />

      <div className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon name="help" className="h-3.5 w-3.5" />
        没有邮件与短信服务，因此本系统不提供「忘记密码」自助找回 —— 只能由管理员重置。
      </div>
    </div>
  );
}
